import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI } from '../utils/api';
import { MessageLog, MessageStats } from '../types';
import MessageDetailModal from '../components/MessageDetailModal';
import WebhookEventsTab from '../components/WebhookEventsTab';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

type Tab = 'messaggi' | 'webhook';

const TABS: { key: Tab; label: string }[] = [
  { key: 'messaggi', label: 'Messaggi' },
  { key: 'webhook', label: 'Eventi Webhook' },
];

interface MessageFilters {
  page: number;
  limit: number;
  channel?: string;
  direction?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

const Messages: React.FC = () => {
  const { user } = useAuth();
  const isHost = user?.is_host || false;

  const [activeTab, setActiveTab] = useState<Tab>('messaggi');
  const [filters, setFilters] = useState<MessageFilters>({
    page: 1,
    limit: 20,
  });
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  // Fetch messages
  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['messages', filters],
    queryFn: async () => {
      const response = await messagesAPI.getMessages(filters);
      return response.data;
    },
    enabled: activeTab === 'messaggi',
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['messageStats'],
    queryFn: async () => {
      const response = await messagesAPI.getStats({ days: 30 });
      return response.data as MessageStats;
    },
  });

  const messages: MessageLog[] = messagesData?.data || [];
  const pagination = messagesData?.pagination;

  const handleFilterChange = (key: keyof MessageFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 20 });
  };

  const hasActiveFilters = filters.channel || filters.direction || filters.search || filters.date_from || filters.date_to;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const getChannelBadge = (channel: string) => {
    if (channel === 'telegram') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
          Telegram
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <DevicePhoneMobileIcon className="h-3 w-3 mr-1" />
        WhatsApp
      </span>
    );
  };

  const getDirectionIcon = (direction: string) => {
    if (direction === 'inbound') {
      return (
        <span className="inline-flex items-center text-blue-600" title="In entrata">
          <ArrowDownLeftIcon className="h-4 w-4" />
        </span>
      );
    }
    return (
      <span className="inline-flex items-center text-orange-600" title="In uscita">
        <ArrowUpRightIcon className="h-4 w-4" />
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      sent: { color: 'bg-gray-100 text-gray-800', label: 'Inviato' },
      delivered: { color: 'bg-blue-100 text-blue-800', label: 'Consegnato' },
      read: { color: 'bg-green-100 text-green-800', label: 'Letto' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Fallito' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const truncateText = (text: string | null | undefined, maxLength: number = 80) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Calculate today's messages from stats
  const todayStr = new Date().toISOString().split('T')[0];
  const todayStats = statsData?.by_day?.find((d) => d.date === todayStr);
  const todayCount = todayStats?.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Messaggi</h1>
        <p className="text-gray-600 mt-1">
          {isHost
            ? 'Visualizza tutti i messaggi della piattaforma'
            : 'Visualizza i messaggi del tuo tenant'}
        </p>
      </div>

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Telegram</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {(statsData.by_channel?.telegram || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DevicePhoneMobileIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">WhatsApp</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {(statsData.by_channel?.whatsapp || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClockIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Oggi</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {todayCount.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'messaggi' && (
        <>
          {/* Filters + Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Cerca nel testo dei messaggi..."
                      value={filters.search || ''}
                      onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Channel filter */}
                <select
                  value={filters.channel || ''}
                  onChange={(e) => handleFilterChange('channel', e.target.value || undefined)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tutti i canali</option>
                  <option value="telegram">Telegram</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>

                {/* Date from */}
                <input
                  type="date"
                  value={filters.date_from?.split('T')[0] || ''}
                  onChange={(e) =>
                    handleFilterChange('date_from', e.target.value ? `${e.target.value}T00:00:00` : undefined)
                  }
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  title="Data da"
                />

                {/* Date to */}
                <input
                  type="date"
                  value={filters.date_to?.split('T')[0] || ''}
                  onChange={(e) =>
                    handleFilterChange('date_to', e.target.value ? `${e.target.value}T23:59:59` : undefined)
                  }
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  title="Data a"
                />

                {/* Clear filters */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    <span>Cancella filtri</span>
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Ora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Canale
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dir.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mittente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Messaggio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    {isHost && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tenant
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={isHost ? 7 : 6} className="px-6 py-12 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                        <p className="mt-2 text-gray-500">Caricamento...</p>
                      </td>
                    </tr>
                  ) : messages.length === 0 ? (
                    <tr>
                      <td colSpan={isHost ? 7 : 6} className="px-6 py-12 text-center">
                        <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300 mx-auto" />
                        <p className="mt-2 text-gray-500">Nessun messaggio trovato</p>
                      </td>
                    </tr>
                  ) : (
                    messages.map((msg) => (
                      <tr
                        key={msg.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedMessageId(msg.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-4 w-4 text-gray-400" />
                            <span>{formatDate(msg.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getChannelBadge(msg.channel)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getDirectionIcon(msg.direction)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {msg.sender_id}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                          {msg.media_type ? (
                            <span className="inline-flex items-center text-xs text-gray-500 italic">
                              [{msg.media_type}] {truncateText(msg.message_text, 60)}
                            </span>
                          ) : (
                            truncateText(msg.message_text)
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(msg.message_status)}
                        </td>
                        {isHost && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {msg.tenant_id.substring(0, 8)}...
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1} -{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} di{' '}
                  {pagination.total} risultati
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleFilterChange('page', pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Pagina {pagination.page} di {pagination.pages}
                  </span>
                  <button
                    onClick={() => handleFilterChange('page', pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'webhook' && <WebhookEventsTab />}

      {/* Message Detail Modal */}
      <MessageDetailModal
        isOpen={!!selectedMessageId}
        onClose={() => setSelectedMessageId(null)}
        messageId={selectedMessageId}
      />
    </div>
  );
};

export default Messages;
